import { useEffect, useRef } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useRestaurant } from '@core/tenant';
import { useBooking } from '@features/booking/state';
import { canBypassGuarantee, getAgentRole } from '@features/booking/utils/agentUtils';

/**
 * Reactive hook that detects when the logged-in user is an agent of the
 * current restaurant, and sets `isAgentReservation` + `agentRole` in
 * booking state.
 *
 * Must be mounted inside both AuthProvider and TenantProvider (e.g. in
 * BookingPageLayout). Does not render anything — only runs an effect.
 *
 * Reacts to auth state changes so that if a user logs in mid-flow and
 * turns out to be an agent, the booking flow recalculates automatically.
 */
export function useAgentDetection(): void {
  const { authState } = useAuth();
  const restaurant = useRestaurant();
  const booking = useBooking();

  const prevIsAgentRef = useRef<boolean | null>(null);

  useEffect(() => {
    const isAgent = canBypassGuarantee(
      authState.roles,
      authState.userRestaurantId,
      restaurant.id,
    );

    // Only update if the value actually changed to avoid unnecessary re-renders
    if (prevIsAgentRef.current === isAgent) return;
    prevIsAgentRef.current = isAgent;

    if (isAgent) {
      const role = getAgentRole(authState.roles);
      booking.updateBooking({
        isAgentReservation: true,
        agentRole: role,
      });
    } else {
      booking.updateBooking({
        isAgentReservation: false,
        agentRole: null,
      });
    }
  }, [authState.roles, authState.userRestaurantId, restaurant.id]); // eslint-disable-line react-hooks/exhaustive-deps
}
